import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card, Row, Col, Typography, Flex, DatePicker, Button, Avatar, Badge, Divider, Spin, Table, Dropdown } from "antd";
import {
  ArrowRight01Icon,
  ArrowUpRight01Icon,
  ArrowDown01Icon,
  FilterIcon,
  CheckmarkCircle01Icon,
  ArrowLeftRightIcon,
  Call02Icon,
  Video01Icon,
  Task01Icon,
  Clock01Icon,
} from "hugeicons-react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import Chart from "chart.js/auto";
import { getCallLogSummary, getCallLogTimeline, getTopEmployees, getInvoiceRevenue } from "../../services/dashboardService";
import { getTodayActivities } from "../../services/activityService";
import { getTodayBreaks } from "../../services/breakService";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Palette matching CRM theme
const PALETTE = {
  incoming: "#3b82f6",
  outgoing: "#f59e0b",
  missed: "#ef4444",
  rejected: "#64748b",
};

const ACTIVITY_CONFIG = {
  call: {
    label: "Call",
    color: "#2563eb",
    icon: <Call02Icon size={13} style={{ verticalAlign: "middle" }} />,
  },
  meeting: {
    label: "Meeting",
    color: "#7e22ce",
    icon: <Video01Icon size={13} style={{ verticalAlign: "middle" }} />,
  },
  task: {
    label: "Task",
    color: "#16a34a",
    icon: <Task01Icon size={13} style={{ verticalAlign: "middle" }} />,
  },
};

function formatActivityTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Formats duration in seconds to "Xh Ym", "Xm Ys" or "Xs"
function formatDuration(seconds = 0) {
  const s = Math.round(Number(seconds) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) {
    return remS > 0 ? `${m}m ${remS}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

const LOCAL_STORAGE_COL_WIDTHS_KEY = "dealvoice_today_activities_col_widths_v2";

const DEFAULT_TODAY_ACTIVITY_COL_WIDTHS = {
  type: 80,
  title: 170,
  company: 150,
  assignedTo: 110,
  activityDate: 95,
};

function getSavedColWidths() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_COL_WIDTHS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_TODAY_ACTIVITY_COL_WIDTHS, ...parsed };
    }
  } catch (e) {
    // Ignore localStorage parse errors
  }
  return DEFAULT_TODAY_ACTIVITY_COL_WIDTHS;
}

const ResizableHeaderCell = (props) => {
  const { onResize, width, children, ...restProps } = props;

  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX;
    const startWidth = width;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startX;
      const nextWidth = Math.max(70, startWidth + deltaX);
      if (onResize) {
        onResize(nextWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width, position: "relative" }}>
      {children}
      <button type="button" aria-label="Resize column" className="table-resizable-handle" onMouseDown={handleMouseDown} />
    </th>
  );
};

/**
 * Interactive Timeline / Performance Call Trend Chart Component
 */
function CallTrendChart({ chartType, labels, data }) {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Clean up any previous instance attached to this canvas or ref
    const existingChart = Chart.getChart(canvasRef.current);
    if (existingChart) {
      existingChart.destroy();
    }
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const chartLabels = labels && labels.length > 0 ? labels : ["09 AM", "10 AM", "11 AM", "12 PM", "01 PM", "02 PM", "03 PM", "04 PM", "05 PM"];
    const chartData = data && data.length > 0 ? data : [0, 0, 0, 0, 0, 0, 0, 0, 0];

    // Create gradient fill
    const grad = ctx.createLinearGradient(0, 0, 0, 180);
    grad.addColorStop(0, "rgba(36, 89, 211, 0.28)");
    grad.addColorStop(1, "rgba(36, 89, 211, 0.0)");

    const isBar = chartType === "bar";

    chartInstance.current = new Chart(canvasRef.current, {
      type: isBar ? "bar" : "line",
      data: {
        labels: chartLabels,
        datasets: [
          {
            label: "Calls",
            data: chartData,
            borderColor: "#2459d3",
            backgroundColor: isBar ? "#2459d3" : grad,
            fill: !isBar,
            tension: isBar ? 0 : 0.45,
            pointRadius: isBar ? 0 : 4,
            pointHoverRadius: 7,
            pointBackgroundColor: "#2459d3",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            borderWidth: isBar ? 0 : 3.5,
            borderRadius: isBar ? 6 : 0,
            barPercentage: isBar ? 0.45 : undefined,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1e293b",
            titleColor: "#ffffff",
            bodyColor: "#94a3b8",
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (item) => {
                const count = typeof item?.raw === "number" ? item.raw : (item?.parsed?.y ?? 0);
                return `${count} Calls`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 11, weight: "500" },
              padding: 6,
            },
            border: { display: false },
          },
          y: {
            display: false,
            beginAtZero: true,
            grid: { display: false },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartType, labels, data]);

  return (
    <div style={{ height: 180, width: "100%", position: "relative" }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

/**
 * Interactive Call Mix Donut / Pie Chart Component
 */
function CallMixDonutChart({ stats }) {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const existingChart = Chart.getChart(canvasRef.current);
    if (existingChart) {
      existingChart.destroy();
    }
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const counts = [stats?.incoming || 0, stats?.outgoing || 0, stats?.missed || 0, stats?.rejected || 0];

    const hasData = counts.some((c) => c > 0);

    chartInstance.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: ["Incoming", "Outgoing", "Missed", "Rejected"],
        datasets: [
          {
            data: hasData ? counts : [0, 0, 0, 1],
            backgroundColor: hasData ? [PALETTE.incoming, PALETTE.outgoing, PALETTE.missed, PALETTE.rejected] : ["#e2e8f0", "#e2e8f0", "#e2e8f0", "#e2e8f0"],
            borderWidth: 2,
            borderColor: "#ffffff",
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "72%",
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1e293b",
            titleColor: "#ffffff",
            bodyColor: "#94a3b8",
            padding: 8,
            cornerRadius: 8,
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [stats]);

  return (
    <div style={{ width: 110, height: 110, position: "relative" }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

function PeriodFilterDropdown({
  selectedPeriod,
  currentPeriodLabel,
  dropdownOpen,
  onOpenChange,
  showCustomFlyout,
  setShowCustomFlyout,
  pendingRange,
  setPendingRange,
  handleSelectPreset,
  handleApplyCustom,
}) {
  const PRESET_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "last_week", label: "Last 7 Days" },
  ];

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={onOpenChange}
      trigger={["click"]}
      placement="bottomRight"
      dropdownRender={() => (
        <div
          style={{
            display: "flex",
            background: "#ffffff",
            borderRadius: 10,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          {/* Main Presets Column */}
          <div
            style={{
              width: 170,
              padding: "8px 6px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              background: "#ffffff",
            }}
          >
            {PRESET_OPTIONS.map((item) => {
              const isSelected = selectedPeriod === item.key;
              return (
                <div
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectPreset(item.key)}
                  onMouseEnter={() => setShowCustomFlyout(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectPreset(item.key);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderRadius: 6,
                    fontSize: "13px",
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? "#2459d3" : "#1e293b",
                    background: isSelected ? "#eff6ff" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{item.label}</span>
                  {isSelected && <CheckmarkCircle01Icon size={14} color="#2459d3" />}
                </div>
              );
            })}

            <Divider style={{ margin: "4px 0" }} />

            {/* Custom Option with Hover Trigger */}
            <div
              role="button"
              tabIndex={0}
              onMouseEnter={() => setShowCustomFlyout(true)}
              onClick={() => setShowCustomFlyout(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowCustomFlyout(true);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: "13px",
                fontWeight: selectedPeriod === "custom" || showCustomFlyout ? 600 : 400,
                color: selectedPeriod === "custom" || showCustomFlyout ? "#2459d3" : "#1e293b",
                background: showCustomFlyout ? "#eff6ff" : selectedPeriod === "custom" ? "#f1f5f9" : "transparent",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                if (!showCustomFlyout && selectedPeriod !== "custom") e.currentTarget.style.background = "#f8fafc";
              }}
              onMouseOut={(e) => {
                if (!showCustomFlyout && selectedPeriod !== "custom") e.currentTarget.style.background = "transparent";
              }}
            >
              <span>Custom</span>
              <ArrowRight01Icon size={13} color={showCustomFlyout ? "#2459d3" : "#94a3b8"} />
            </div>
          </div>

          {/* Appended Flyout Sub-menu (Date Picker & Presets up to Today) */}
          {showCustomFlyout && (
            <div
              style={{
                width: 320,
                padding: "14px 16px",
                borderLeft: "1px solid #f1f5f9",
                background: "#fafbfd",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text strong style={{ fontSize: "13px", color: "#1e293b" }}>
                  Custom Range
                </Text>
                <Text type="secondary" style={{ fontSize: "11px" }}>
                  Select dates up to Today
                </Text>
              </div>

              {/* Quick shortcut chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <Button
                  size="small"
                  style={{ fontSize: "11px", borderRadius: 4 }}
                  onClick={() => setPendingRange([dayjs().startOf("month"), dayjs()])}
                >
                  This Month → Today
                </Button>
                <Button
                  size="small"
                  style={{ fontSize: "11px", borderRadius: 4 }}
                  onClick={() => setPendingRange([dayjs().subtract(14, "day"), dayjs()])}
                >
                  Last 14 Days → Today
                </Button>
                <Button
                  size="small"
                  style={{ fontSize: "11px", borderRadius: 4 }}
                  onClick={() => setPendingRange([dayjs().subtract(30, "day"), dayjs()])}
                >
                  Last 30 Days → Today
                </Button>
              </div>

              <div>
                <Text type="secondary" style={{ fontSize: "11px", display: "block", marginBottom: 6 }}>
                  Start date → End date:
                </Text>
                <RangePicker
                  style={{ width: "100%" }}
                  value={pendingRange}
                  onChange={(dates) => setPendingRange(dates)}
                  allowClear={false}
                  disabledDate={(current) => current && current > dayjs().endOf("day")}
                />
              </div>

              {/* Footer actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShowCustomFlyout(false);
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  disabled={!pendingRange || pendingRange.length !== 2}
                  onClick={handleApplyCustom}
                  style={{ background: "#2459d3" }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    >
      <Button
        type="text"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 32,
          padding: "0 8px",
          borderRadius: 6,
          fontWeight: 600,
          color: "#1e293b",
          background: "transparent",
          border: "none",
          boxShadow: "none",
          cursor: "pointer",
        }}
        aria-label="Select period filter"
      >
        <FilterIcon size={15} color="#2459d3" />
        <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 600 }}>{currentPeriodLabel}</span>
        <ArrowDown01Icon size={12} color="#64748b" style={{ marginLeft: 2 }} />
      </Button>
    </Dropdown>
  );
}

function AdminOverview({ onNavigateTab, onReady }) {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const initialLoadDone = useRef(false);

  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [customRange, setCustomRange] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCustomFlyout, setShowCustomFlyout] = useState(false);
  const [pendingRange, setPendingRange] = useState(() => [dayjs().subtract(7, "day"), dayjs()]);
  const [chartType, setChartType] = useState("line");
  const [loading, setLoading] = useState(false);

  const currentPeriodLabel = useMemo(() => {
    if (selectedPeriod === "today") return "Today";
    if (selectedPeriod === "yesterday") return "Yesterday";
    if (selectedPeriod === "last_week") return "Last 7 Days";
    if (selectedPeriod === "custom" && customRange?.length === 2) {
      return `${customRange[0].format("DD MMM")} – ${customRange[1].format("DD MMM, YYYY")}`;
    }
    return "Custom Range";
  }, [selectedPeriod, customRange]);

  const handleSelectPreset = useCallback((periodKey) => {
    setSelectedPeriod(periodKey);
    setCustomRange(null);
    setShowCustomFlyout(false);
    setDropdownOpen(false);
  }, []);

  const handleApplyCustom = useCallback(() => {
    if (pendingRange && pendingRange.length === 2) {
      setCustomRange(pendingRange);
      setSelectedPeriod("custom");
      setShowCustomFlyout(false);
      setDropdownOpen(false);
    }
  }, [pendingRange]);

  const handleOpenChange = useCallback(
    (open) => {
      setDropdownOpen(open);
      if (!open) {
        setShowCustomFlyout(false);
      } else if (selectedPeriod === "custom" && customRange?.length === 2) {
        setPendingRange(customRange);
      }
    },
    [selectedPeriod, customRange],
  );

  // Live Middleware Stats
  const [stats, setStats] = useState({
    total: 0,
    connected: 0,
    totalDuration: 0,
    missed: 0,
    rejected: 0,
    incoming: 0,
    outgoing: 0,
    incomingDuration: 0,
    outgoingDuration: 0,
  });

  const [timelineLabels, setTimelineLabels] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [topEmployees, setTopEmployees] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [activityColWidths, setActivityColWidths] = useState(getSavedColWidths);
  const [breakLogs, setBreakLogs] = useState([]);
  const [revenue, setRevenue] = useState({
    totalAmount: 0,
    amountPaid: 0,
    balanceDue: 0,
    invoiceCount: 0,
  });

  // Fetch Dashboard Live Data
  const loadDashboardData = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const formattedRange = customRange?.length === 2 ? [customRange[0].format("YYYY-MM-DD"), customRange[1].format("YYYY-MM-DD")] : null;

      const results = await Promise.allSettled([
        getCallLogSummary(effectiveCompanyCode, selectedPeriod, formattedRange),
        getCallLogTimeline(effectiveCompanyCode, selectedPeriod, formattedRange),
        getTopEmployees(effectiveCompanyCode, selectedPeriod),
        getInvoiceRevenue(effectiveCompanyCode),
        getTodayActivities(effectiveCompanyCode),
        getTodayBreaks(effectiveCompanyCode),
      ]);

      const summaryRes = results[0].status === "fulfilled" ? results[0].value : null;
      const timelineRes = results[1].status === "fulfilled" ? results[1].value : [];
      const employeesRes = results[2].status === "fulfilled" ? results[2].value : [];
      const invoiceRes = results[3].status === "fulfilled" ? results[3].value : null;
      const activitiesRes = results[4].status === "fulfilled" ? results[4].value : null;
      const breaksRes = results[5].status === "fulfilled" ? results[5].value : null;

      if (summaryRes) {
        setStats(summaryRes);
      }

      if (activitiesRes?.success && Array.isArray(activitiesRes.data)) {
        setTodayActivities(activitiesRes.data);
      } else {
        setTodayActivities([]);
      }

      if (breaksRes?.success && Array.isArray(breaksRes.logs)) {
        setBreakLogs(breaksRes.logs);
      } else {
        setBreakLogs([]);
      }

      if (timelineRes && timelineRes.length > 0) {
        const isHourly = timelineRes.some((t) => t._isHourly);
        const rawData = timelineRes.map((t) => (t.incoming || 0) + (t.outgoing || 0) + (t.missed || 0) + (t.rejected || 0));

        let slicedTimeline = timelineRes;
        let slicedData = rawData;

        if (isHourly) {
          const firstActiveIdx = rawData.findIndex((c) => c > 0);
          const lastActiveIdx = rawData.reduce((acc, c, idx) => (c > 0 ? idx : acc), -1);

          if (firstActiveIdx !== -1) {
            const nowHour = new Date().getHours();
            const startIdx = firstActiveIdx; // Start exactly at the hour call data starts
            const endIdx = Math.min(timelineRes.length - 1, Math.max(lastActiveIdx + 1, nowHour, startIdx + 3));
            slicedTimeline = timelineRes.slice(startIdx, endIdx + 1);
            slicedData = rawData.slice(startIdx, endIdx + 1);
          } else {
            // Default active business hours if no calls yet
            slicedTimeline = timelineRes.slice(9, 19);
            slicedData = rawData.slice(9, 19);
          }
        }

        const labels = slicedTimeline.map((t) => {
          if (t._isHourly && t.date) {
            const h = new Date(t.date).getHours();
            return `${h % 12 || 12} ${h >= 12 ? "PM" : "AM"}`;
          }
          return t.date ? t.date.slice(5) : "";
        });

        setTimelineLabels(labels);
        setTimelineData(slicedData);
      }

      if (Array.isArray(employeesRes) && employeesRes.length > 0) {
        const formatted = employeesRes.map((emp, index) => ({
          rank: index + 1,
          name: emp.name || `Representative ${index + 1}`,
          mobile: emp.mobile || emp.employeeId || "—",
          profilePhoto: emp.profilePhoto || "",
          calls: (emp.incoming || 0) + (emp.outgoing || 0) + (emp.missed || 0) + (emp.rejected || 0) || 0,
          duration: formatDuration(emp.totalDuration || 0),
          connected: emp.connected || 0,
        }));
        setTopEmployees(formatted);
      } else {
        setTopEmployees([]);
      }

      if (invoiceRes && (invoiceRes.invoiceCount > 0 || invoiceRes.totalAmount > 0 || invoiceRes.amountPaid > 0)) {
        setRevenue(invoiceRes);
      }
      // If invoiceRes is null or all zeros, keep existing revenue state intact
    } catch (err) {
      console.error("Failed to load dashboard data from middleware:", err);
    } finally {
      setLoading(false);
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        if (onReadyRef.current) {
          onReadyRef.current();
        }
      }
    }
  }, [effectiveCompanyCode, selectedPeriod, customRange]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const totalCalls = Number(stats?.total) || 0;
  const connectedCalls = Number(stats?.connected) || 0;
  const missedCalls = Number(stats?.missed) || 0;
  const outgoingCalls = Number(stats?.outgoing) || 0;

  const connectRate = totalCalls > 0 ? Math.round((connectedCalls / totalCalls) * 100) : 0;
  const missedRate = totalCalls > 0 ? Math.round((missedCalls / totalCalls) * 100) : 0;
  const outboundShare = totalCalls > 0 ? Math.round((outgoingCalls / totalCalls) * 100) : 0;
  const incomingShare = 100 - outboundShare;

  const handleResizeActivityCol = useCallback(
    (key) => (width) => {
      setActivityColWidths((prev) => {
        const next = { ...prev, [key]: width };
        try {
          localStorage.setItem(LOCAL_STORAGE_COL_WIDTHS_KEY, JSON.stringify(next));
        } catch (e) {
          // Ignore localStorage errors
        }
        return next;
      });
    },
    [],
  );

  const totalActivityTableWidth = useMemo(() => Object.values(activityColWidths).reduce((sum, w) => sum + (Number(w) || 90), 0), [activityColWidths]);

  const todayActivityColumns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: activityColWidths.type,
        onHeaderCell: () => ({
          width: activityColWidths.type,
          onResize: handleResizeActivityCol("type"),
        }),
        render: (type) => {
          const typeCfg = ACTIVITY_CONFIG[type] || ACTIVITY_CONFIG.task;
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.8rem",
                fontWeight: 500,
                color: typeCfg.color,
                whiteSpace: "nowrap",
              }}
            >
              {typeCfg.icon}
              {typeCfg.label}
            </span>
          );
        },
      },
      {
        title: "Activity / Subject",
        dataIndex: "title",
        key: "title",
        width: activityColWidths.title,
        ellipsis: true,
        onHeaderCell: () => ({
          width: activityColWidths.title,
          onResize: handleResizeActivityCol("title"),
        }),
        render: (title) => (
          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={title || "Untitled Activity"}>
            <Text style={{ fontSize: "0.8125rem", fontWeight: 500, color: "#0f172a" }}>
              {title || "Untitled Activity"}
            </Text>
          </div>
        ),
      },
      {
        title: "Lead / Company",
        key: "company",
        width: activityColWidths.company,
        ellipsis: true,
        onHeaderCell: () => ({
          width: activityColWidths.company,
          onResize: handleResizeActivityCol("company"),
        }),
        render: (_, record) => {
          const comp = record.leadCompanyName || record.companyName || record.lead?.companyName;
          const contact = record.contactName || record.lead?.contactName;
          const display = comp && contact ? `${comp} (${contact})` : comp || contact;
          if (!display) {
            return <span style={{ color: "#cbd5e1", fontSize: "0.8rem" }}>—</span>;
          }
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={display}>
              <Text style={{ fontSize: "0.8125rem", color: "#334155" }}>{display}</Text>
            </div>
          );
        },
      },
      {
        title: "Assigned To",
        key: "assignedTo",
        width: activityColWidths.assignedTo,
        ellipsis: true,
        onHeaderCell: () => ({
          width: activityColWidths.assignedTo,
          onResize: handleResizeActivityCol("assignedTo"),
        }),
        render: (_, record) => {
          const empName = record.assignedEmployeeName || record.employeeName || record.employee?.name;
          if (!empName) {
            return <span style={{ color: "#cbd5e1", fontSize: "0.8rem" }}>—</span>;
          }
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={empName}>
              <Text style={{ fontSize: "0.8125rem", color: "#475569" }}>{empName}</Text>
            </div>
          );
        },
      },
      {
        title: "Time",
        dataIndex: "activityDate",
        key: "activityDate",
        width: activityColWidths.activityDate,
        align: "right",
        onHeaderCell: () => ({
          width: activityColWidths.activityDate,
          onResize: handleResizeActivityCol("activityDate"),
        }),
        render: (dateStr) => {
          const formattedTime = formatActivityTime(dateStr);
          if (!formattedTime) {
            return <span style={{ color: "#cbd5e1", fontSize: "0.78rem" }}>—</span>;
          }
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.75rem",
                color: "#64748b",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              <Clock01Icon size={12} style={{ color: "#94a3b8" }} />
              {formattedTime}
            </span>
          );
        },
      },
    ],
    [activityColWidths, handleResizeActivityCol],
  );

  return (
    <Spin spinning={loading} description="Loading dashboard...">
      <Flex vertical gap={24} className="admin-overview-container" style={{ width: "100%", paddingBottom: "1.5rem" }}>
        {/* ── MAIN CHARTS & TOP EMPLOYEE ROW ── */}
        <Row gutter={[16, 16]}>
          {/* Left 15 cols: Call Activity, Trend, Mix & Telemetry */}
          <Col xs={24} xl={15}>
            <Flex vertical gap={16}>
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                }}
                styles={{ body: { padding: "20px" } }}
              >
                <Row gutter={[24, 24]}>
                  {/* Performance Call Trend (Line / Bar) */}
                  <Col xs={24} md={14}>
                    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>
                          Performance
                        </Text>
                        <Title level={4} style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
                          Call Trend
                        </Title>
                      </div>
                      <Button type="text" size="small" icon={<ArrowLeftRightIcon size={13} style={{ verticalAlign: "middle" }} />} onClick={() => setChartType(chartType === "line" ? "bar" : "line")} style={{ color: "#64748b", fontSize: "0.8rem", display: "inline-flex", alignItems: "center" }}>
                        {chartType === "line" ? "Bar" : "Line"}
                      </Button>
                    </Flex>

                    {/* Real Chart.js Timeline Graph with Live Middleware Data */}
                    <CallTrendChart chartType={chartType} labels={timelineLabels} data={timelineData} />
                  </Col>

                  {/* Distribution Call Mix (Donut / Pie) */}
                  <Col xs={24} md={10} className="call-mix-split-col">
                    <Flex vertical justify="space-between" style={{ height: "100%" }}>
                      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>
                            Distribution
                          </Text>
                          <Title level={4} style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
                            Call Mix
                          </Title>
                        </div>
                        <PeriodFilterDropdown
                          selectedPeriod={selectedPeriod}
                          currentPeriodLabel={currentPeriodLabel}
                          dropdownOpen={dropdownOpen}
                          onOpenChange={handleOpenChange}
                          showCustomFlyout={showCustomFlyout}
                          setShowCustomFlyout={setShowCustomFlyout}
                          pendingRange={pendingRange}
                          setPendingRange={setPendingRange}
                          handleSelectPreset={handleSelectPreset}
                          handleApplyCustom={handleApplyCustom}
                        />
                      </Flex>

                      <Flex align="center" justify="space-between" gap={16}>
                        {/* Real Chart.js Donut Pie Chart */}
                        <CallMixDonutChart stats={stats} />

                        {/* Legend */}
                        <Flex vertical gap={6} style={{ fontSize: "0.8rem", flex: 1 }}>
                          <Flex align="center" justify="space-between">
                            <Flex align="center" gap={6}>
                              <Badge color={PALETTE.incoming} />
                              <Text type="secondary">Incoming</Text>
                            </Flex>
                            <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                              {(Number(stats?.incoming) || 0).toLocaleString()}
                            </Text>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Flex align="center" gap={6}>
                              <Badge color={PALETTE.outgoing} />
                              <Text type="secondary">Outgoing</Text>
                            </Flex>
                            <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                              {(Number(stats?.outgoing) || 0).toLocaleString()}
                            </Text>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Flex align="center" gap={6}>
                              <Badge color={PALETTE.missed} />
                              <Text type="secondary">Missed</Text>
                            </Flex>
                            <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                              {(Number(stats?.missed) || 0).toLocaleString()}
                            </Text>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Flex align="center" gap={6}>
                              <Badge color={PALETTE.rejected} />
                              <Text type="secondary">Rejected</Text>
                            </Flex>
                            <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                              {(Number(stats?.rejected) || 0).toLocaleString()}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Col>
                </Row>

                <Divider style={{ margin: "20px 0 16px" }} />

                {/* Integrated Telemetry Metrics Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 0,
                    border: "1px solid #f1f5f9",
                    borderRadius: 8,
                    backgroundColor: "#f8fafc",
                    overflow: "hidden",
                  }}
                >
                  {[
                    {
                      label: "Total Calls",
                      value: (Number(stats?.total) || 0).toLocaleString(),
                      sub: `${outboundShare}% out · ${incomingShare}% in`,
                    },
                    {
                      label: "Connected",
                      value: (Number(stats?.connected) || 0).toLocaleString(),
                      sub: `${connectRate}% connect rate`,
                    },
                    {
                      label: "Missed / Lost",
                      value: ((Number(stats?.missed) || 0) + (Number(stats?.rejected) || 0)).toLocaleString(),
                      sub: `${missedRate}% missed calls`,
                      color: (Number(stats?.missed) || 0) + (Number(stats?.rejected) || 0) > 0 ? "#ef4444" : undefined,
                    },
                    {
                      label: "Talk Duration",
                      value: formatDuration(stats?.totalDuration || 0),
                      sub: `Avg ${totalCalls > 0 ? formatDuration((stats?.totalDuration || 0) / totalCalls) : "0s"} / call`,
                    },
                  ].map((item, idx) => (
                    <div
                      key={item.label}
                      style={{
                        background: "#ffffff",
                        padding: "12px 14px",
                        borderRight: idx < 3 ? "1px solid #f1f5f9" : "none",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 500 }}>{item.label}</Text>
                      <div
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          color: item.color || "#0f172a",
                          fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.015em",
                          margin: "4px 0 2px",
                        }}
                      >
                        {item.value}
                      </div>
                      <Text style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{item.sub}</Text>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Today's Quick Activities Card (Below Call Trend) */}
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                }}
                styles={{ body: { padding: "16px" } }}
                title={
                  <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                    <Flex align="center" gap={8}>
                      <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Today's Activities</span>
                      {todayActivities.length > 0 && (
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            padding: "1px 7px",
                            borderRadius: 10,
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                          }}
                        >
                          {todayActivities.length}
                        </span>
                      )}
                    </Flex>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => onNavigateTab?.("activities")}
                      style={{ padding: "0 6px", fontSize: "0.8rem", color: "#64748b", display: "inline-flex", alignItems: "center", gap: 3 }}
                    >
                      Calendar <ArrowUpRight01Icon size={12} />
                    </Button>
                  </Flex>
                }
              >
                <Table
                  className="today-activities-table hide-scrollbar"
                  size="middle"
                  dataSource={todayActivities}
                  columns={todayActivityColumns}
                  rowKey={(r) => r._id || r.id || `${r.title}-${r.activityDate}`}
                  pagination={todayActivities.length > 5 ? { pageSize: 5, size: "small" } : false}
                  scroll={{ x: totalActivityTableWidth }}
                  components={{
                    header: {
                      cell: ResizableHeaderCell,
                    },
                  }}
                  onRow={(record) => ({
                    onClick: () => onNavigateTab?.("activities", record),
                    style: { cursor: "pointer" },
                  })}
                  locale={{
                    emptyText: (
                      <Flex justify="center" align="center" style={{ padding: "1.2rem 0" }}>
                        <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                          No activities scheduled for today
                        </Text>
                      </Flex>
                    ),
                  }}
                />
              </Card>
            </Flex>
          </Col>

          {/* Right 9 cols: Top Employees Leaderboard & Revenue Invoices */}
          <Col xs={24} xl={9}>
            <Flex vertical gap={16}>
              {/* Top Employee Leaderboard Card */}
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                }}
                styles={{ body: { padding: "18px" } }}
                title={
                  <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                    <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Top Representatives</span>
                    <Button type="link" size="small" onClick={() => onNavigateTab?.("employees")} style={{ padding: 0, fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: 3 }}>
                      See all <ArrowUpRight01Icon size={12} />
                    </Button>
                  </Flex>
                }
              >
                <Flex vertical gap={8}>
                  {topEmployees.length > 0 ? (
                    topEmployees.slice(0, 4).map((emp, index) => (
                      <Flex
                        key={emp.rank || index}
                        align="center"
                        justify="space-between"
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          transition: "background 0.15s ease",
                        }}
                      >
                        <Flex align="center" gap={10}>
                          <Avatar
                            size={32}
                            src={emp.profilePhoto || undefined}
                            style={{ backgroundColor: "#3b82f6", fontSize: "0.8rem", flexShrink: 0 }}
                          >
                            {!emp.profilePhoto && (emp.name || "E").charAt(0).toUpperCase()}
                          </Avatar>
                          <Flex vertical>
                            <Text strong style={{ fontSize: "0.825rem", color: "#0f172a", lineHeight: 1.2 }}>
                              {emp.name || "Representative"}
                            </Text>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              {emp.mobile}
                            </Text>
                          </Flex>
                        </Flex>

                        <Flex vertical align="end">
                          <Text strong style={{ fontSize: "0.825rem", color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                            {emp.calls} calls
                          </Text>
                          <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                            {emp.duration}
                          </Text>
                        </Flex>
                      </Flex>
                    ))
                  ) : (
                    <Flex justify="center" align="center" style={{ padding: "1.5rem 0" }}>
                      <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                        No representative activity recorded
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Card>

              {/* Revenue & Invoice Stats Card */}
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                }}
                styles={{ body: { padding: "18px" } }}
                title={<span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Financial Overview</span>}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr auto 1fr",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Text style={{ fontSize: "0.72rem", color: "#64748b" }}>Invoiced ({Number(revenue?.invoiceCount) || 0})</Text>
                    <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>₹{Math.round(Number(revenue?.totalAmount) || 0).toLocaleString("en-IN")}</div>
                  </div>

                  <Divider type="vertical" style={{ height: 36, borderColor: "#e2e8f0", margin: 0 }} />

                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Text style={{ fontSize: "0.72rem", color: "#64748b" }}>Collected</Text>
                    <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>₹{Math.round(Number(revenue?.amountPaid) || 0).toLocaleString("en-IN")}</div>
                  </div>

                  <Divider type="vertical" style={{ height: 36, borderColor: "#e2e8f0", margin: 0 }} />

                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Text style={{ fontSize: "0.72rem", color: "#64748b" }}>Balance Due</Text>
                    <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>₹{Math.round(Number(revenue?.balanceDue) || 0).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              </Card>

              {/* Break Logs Card */}
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                }}
                styles={{ body: { padding: "16px 18px" } }}
                title={
                  <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                    <Flex align="center" gap={8}>
                      <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Break Log</span>
                      {breakLogs.length > 0 && <Badge count={breakLogs.length} style={{ backgroundColor: "#f59e0b" }} />}
                    </Flex>
                    <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                      Today
                    </Text>
                  </Flex>
                }
              >
                <Flex vertical gap={10}>
                  {breakLogs.length > 0 ? (
                    breakLogs.map((log) => {
                      const totalSec = log.totalSeconds || 0;
                      const breaksCount = Array.isArray(log.breaks) ? log.breaks.length : 1;
                      const totalFormatted = formatDuration(totalSec);

                      return (
                        <Flex
                          key={log._id || log.employeeId}
                          align="center"
                          justify="space-between"
                          style={{
                            padding: "8px 12px",
                            background: "#f8fafc",
                            border: "1px solid #f1f5f9",
                            borderRadius: 8,
                          }}
                        >
                          <Flex align="center" gap={10}>
                            <Avatar
                              size={28}
                              src={log.profilePhoto || undefined}
                              style={{ backgroundColor: "#f59e0b", fontSize: "0.75rem", flexShrink: 0 }}
                            >
                              {!log.profilePhoto && (log.employeeName || "E").charAt(0).toUpperCase()}
                            </Avatar>
                            <Flex vertical>
                              <Text strong style={{ fontSize: "0.825rem", color: "#0f172a", lineHeight: 1.2 }}>
                                {log.employeeName || "Employee"}
                              </Text>
                              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                {breaksCount} {breaksCount === 1 ? "break" : "breaks"}
                              </Text>
                            </Flex>
                          </Flex>

                          <Flex vertical align="end">
                            <Text strong style={{ fontSize: "0.825rem", color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                              {totalFormatted}
                            </Text>
                          </Flex>
                        </Flex>
                      );
                    })
                  ) : (
                    <Flex justify="center" align="center" style={{ padding: "1.2rem 0" }}>
                      <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                        No breaks recorded today
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Card>
            </Flex>
          </Col>
        </Row>
      </Flex>
    </Spin>
  );
}

export default AdminOverview;
